import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import Uppy from '@uppy/core'
import XHRUpload from '@uppy/xhr-upload'
import ImageEditor from '@uppy/image-editor'
import { Dashboard } from '@uppy/react'
import '@uppy/core/dist/style.min.css'
import '@uppy/dashboard/dist/style.min.css'
import '@uppy/image-editor/dist/style.min.css'
import '../transfer/uppy/upload-dashboard.css'
import '../sso-workspace/sso-workspace.scss'
import './image-upload.scss'
import { getAccessToken } from '../../storage/auth'
import { ensureFreshAccessToken } from '../api.service'
import { usePageContext } from '../../context/page.context'
import { useNotificationContext } from '../../context/notification/context'
import { buildImageUploadConfig, IMAGE_MIME_TYPES, MAX_SOURCE_SIZE, THUMBNAIL_OUTPUT } from './image-upload-config'
import { renderOutput, renamed } from './image-output'
import { uploadFailure } from './upload-failure'
import { serverMessage } from '../error/server-message'
import { ErrorReference } from '../error/error-reference'
import { ConfirmDialog } from '../sso-workspace/confirm-dialog'
import { IconCheck } from '../icon/icon'
import { agoText, dateTimeText } from '../date/local-time'
import { useMinuteClock } from '../date/use-minute-clock'

export const UPLOAD_FAILED = "The image couldn't be uploaded. Try again in a moment."
export const NOT_AN_IMAGE = 'Choose a JPEG or PNG picture.'
export const sourceTooLarge = size =>
  `That picture is ${(size / (1024 * 1024)).toFixed(1)} MB; the largest accepted is ${Math.round(MAX_SOURCE_SIZE / (1024 * 1024))} MB.`

export const PHASE = { IDLE: 'idle', CROPPING: 'cropping', SAVING: 'saving' }

const capitalized = text => text.charAt(0).toUpperCase() + text.slice(1)

let previewSeq = 0

// The admin's one image uploader (node thumbnails, logos), on Uppy:
//
//   current picture in its frame -> Upload/Replace... (the browser's file
//   picker, added to Uppy) -> crop, locked to the aspect, in Uppy's image
//   editor with a live preview -> Save <noun>: Uppy's own Save writes the
//   crop and the upload starts at once (no second Upload step) -> the stored
//   picture in the frame, "<Noun> saved".
//
// Before any bytes go up, the pre-processor redraws the crop at the fixed
// output size and holds the result to the stored limit (image-output.js),
// then resolves the per-site target (async, so a site can create a DB row
// first -- node thumbnails). Inline only: never the global transfers tray.
//
// The picture shown is the stored one (previewBlob): a site fetches it again
// in onUploaded, which runs only once the upload has succeeded. A failed
// upload leaves it as it was, says why (with its reference) and calls
// onUploadFailed, where a site undoes what it set up for the upload.
//
// The image saves on its own; a page's form (its Update) never does. A site
// that knows when the stored picture was saved passes updatedAt, shown as the
// picture's own "last updated", apart from the record's.
export const UppyImageUpload = ({
  previewBlob,
  aspect,
  output = THUMBNAIL_OUTPUT,
  maxFileSize,
  noun = 'image',
  hint,
  replaceNote,
  removeBody,
  frameClassName = '',
  disabled,
  resolveTarget,
  onUploaded,
  onUploadFailed,
  onDelete,
  updatedAt,
}) => {
  const [w, h] = aspect
  const Noun = capitalized(noun)

  // One class per uploader for cropper.js's live preview (it takes a selector).
  const [previewClass] = useState(() => `exr-iu-preview-${++previewSeq}`)
  const config = useMemo(
    () => buildImageUploadConfig({ aspect, maxFileSize, output, preview: `.${previewClass}` }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [w, h, maxFileSize, output, previewClass]
  )

  // Refs keep the latest callbacks visible to the one Uppy instance.
  const resolveTargetRef = useRef(resolveTarget)
  const onUploadedRef = useRef(onUploaded)
  const onUploadFailedRef = useRef(onUploadFailed)
  useEffect(() => { resolveTargetRef.current = resolveTarget }, [resolveTarget])
  useEffect(() => { onUploadedRef.current = onUploaded }, [onUploaded])
  useEffect(() => { onUploadFailedRef.current = onUploadFailed }, [onUploadFailed])

  const notification = useNotificationContext()
  const notificationRef = useRef(notification)
  useEffect(() => { notificationRef.current = notification }, [notification])

  const [phase, setPhase] = useState(PHASE.IDLE)
  const [fileID, setFileID] = useState(null)
  // The crop being saved, shown in the frame until the stored one is back.
  const [pendingBlob, setPendingBlob] = useState(null)
  // What went wrong last: { text, failure } -- failure, when the server
  // answered, carries the request id for <ErrorReference>.
  const [problem, setProblem] = useState(null)
  const [saved, setSaved] = useState(false)

  // Tracks unmount so deferred work never touches a destroyed instance.
  const destroyedRef = useRef(false)
  // Back to the frame after a save, however it ended (set with the instance).
  const finishRef = useRef(null)

  const [uppy] = useState(() => {
    // Back to the frame: the file leaves Uppy once the current render is
    // done (Dashboard may still be drawing it; removing it synchronously
    // crashed ImageEditor.canEditFile on an undefined file).
    const finish = () => {
      setPhase(PHASE.IDLE)
      setFileID(null)
      setPendingBlob(null)
      setTimeout(() => { if (!destroyedRef.current) instance.cancelAll() }, 0)
    }

    const instance = new Uppy({
      autoProceed: false,
      restrictions: config.restrictions,
      // Only a saved crop uploads. Nothing but Save starts an upload here,
      // so this is a guard, not a step the person ever meets.
      onBeforeUpload: files => !Object.values(files).some(f => !f.meta.cropped),
    })

    instance.use(ImageEditor, config.imageEditor)

    // Save = upload. @uppy/image-editor's save() writes the crop back into
    // the file and emits file-editor:complete with the updated file
    // (lib/ImageEditor.js); that is when the upload starts.
    instance.on('file-editor:complete', async updatedFile => {
      instance.setFileMeta(updatedFile.id, { cropped: true })
      setPendingBlob(updatedFile.data || null)
      try {
        await instance.upload()
      } catch (err) {
        // Refused before any bytes went up (the pre-processor said why).
        finish()
      }
    })

    // formData:false sends the file as the raw request body -- byte-identical
    // to what the backends' GetBody() reads. The endpoint here is a required
    // placeholder; the pre-processor sets the real per-file target.
    instance.use(XHRUpload, {
      endpoint: '/',
      formData: false,
      headers: () => ({ Authorization: getAccessToken() }),
      getResponseData: () => ({}),
    })

    // Just before upload: the fixed output (exact size, held to the stored
    // limit), then the target -- refresh the token if needed (XHR bypasses
    // the axios refresh interceptor) and stamp each file with its endpoint
    // + method via the documented file.xhrUpload override. A refusal here
    // fails the upload before any bytes are sent, and says so.
    instance.addPreProcessor(async fileIDs => {
      setProblem(null)
      try {
        for (const id of fileIDs) {
          const file = instance.getFile(id)
          const blob = await renderOutput(file.data, { output: config.output, maxFileSize: config.maxFileSize })
          const name = renamed(file.name, blob.type)
          instance.setFileState(id, {
            data: new File([blob], name, { type: blob.type }),
            size: blob.size,
            type: blob.type,
          })
        }
        await ensureFreshAccessToken()
        const { url, method } = await resolveTargetRef.current()
        fileIDs.forEach(id => instance.setFileState(id, { xhrUpload: { endpoint: url, method } }))
      } catch (err) {
        const failure = err || {}
        setProblem(
          failure.local
            ? { text: failure.message, failure: null }
            : { text: serverMessage(failure, UPLOAD_FAILED), failure }
        )
        onUploadFailedRef.current && (await onUploadFailedRef.current(failure))
        throw err
      }
    })

    finishRef.current = finish
    return instance
  })

  useEffect(() => {
    destroyedRef.current = false
    // The bytes were refused (or never arrived): Uppy hands over the
    // XMLHttpRequest, which carries the status, the body and the request id.
    const errorHandler = (file, error, xhr) => {
      const failure = uploadFailure(xhr || (error && error.request))
      setProblem({ text: serverMessage(failure, UPLOAD_FAILED), failure })
      onUploadFailedRef.current && onUploadFailedRef.current(failure)
    }
    const completeHandler = async result => {
      if (result && result.successful && result.successful.length) {
        setProblem(null)
        onUploadedRef.current && (await onUploadedRef.current())
        if (destroyedRef.current) return
        setSaved(true)
        const toast = notificationRef.current && notificationRef.current.toast
        toast && toast.notify(`${Noun} saved`)
      }
      if (!destroyedRef.current) finishRef.current()
    }
    uppy.on('upload-error', errorHandler)
    uppy.on('complete', completeHandler)
    return () => {
      uppy.off('upload-error', errorHandler)
      uppy.off('complete', completeHandler)
      destroyedRef.current = true
      uppy.destroy()
    }
  }, [uppy, Noun])

  // Uppy's editor opens once the crop stage is on screen (cropper.js
  // measures its container when it starts).
  useLayoutEffect(() => {
    if (phase !== PHASE.CROPPING || !fileID) return
    const dash = uppy.getPlugin('Dashboard')
    const file = uppy.getFile(fileID)
    if (dash && file) dash.openFileEditor(file)
  }, [phase, fileID, uppy])

  // The picture in the frame: the crop being saved, else the stored one.
  const shownBlob = phase === PHASE.SAVING && pendingBlob ? pendingBlob : previewBlob
  const shownURL = useMemo(
    () => (shownBlob && shownBlob.size ? URL.createObjectURL(shownBlob) : null),
    [shownBlob]
  )
  useEffect(() => () => { shownURL && URL.revokeObjectURL(shownURL) }, [shownURL])
  const hasCurrent = !!(previewBlob && previewBlob.size)
  // The page's one clock, as the course cards' "Updated": "just now" also
  // covers a stored time a little ahead of this browser's clock.
  const now = useMinuteClock()
  const updatedText = updatedAt ? agoText(updatedAt, { now }) : ''

  const inputRef = useRef(null)

  const handleChoose = () => {
    setProblem(null)
    setSaved(false)
    inputRef.current && inputRef.current.click()
  }

  const handlePicked = e => {
    const picked = e.target.files && e.target.files[0]
    e.target.value = ''
    if (!picked) return
    if (!IMAGE_MIME_TYPES.includes(picked.type)) {
      setProblem({ text: NOT_AN_IMAGE, failure: null })
      return
    }
    if (picked.size > MAX_SOURCE_SIZE) {
      setProblem({ text: sourceTooLarge(picked.size), failure: null })
      return
    }
    uppy.cancelAll()
    let id
    try {
      id = uppy.addFile({ source: 'Local', name: picked.name, type: picked.type, data: picked })
    } catch (err) {
      setProblem({ text: (err && err.message) || NOT_AN_IMAGE, failure: null })
      return
    }
    setFileID(id)
    setPhase(PHASE.CROPPING)
  }

  const handleCancel = () => {
    const dash = uppy.getPlugin('Dashboard')
    const file = fileID && uppy.getFile(fileID)
    if (file) uppy.emit('file-editor:cancel', file)
    if (dash) dash.closeFileEditor()
    uppy.cancelAll()
    setFileID(null)
    setPhase(PHASE.IDLE)
  }

  // Uppy's own Save (Dashboard.saveFileEditor -> ImageEditor.save); the
  // upload follows on file-editor:complete.
  const handleSave = () => {
    setProblem(null)
    setPhase(PHASE.SAVING)
    const dash = uppy.getPlugin('Dashboard')
    try {
      if (dash) dash.saveFileEditor()
    } catch (err) {
      // The editor wasn't ready (the picture still loading): stay on it.
      setPhase(PHASE.CROPPING)
    }
  }

  // Remove: confirmed first (the workspace's ConfirmDialog); a failure keeps
  // the dialog open with the server's sentence and its reference.
  const [removing, setRemoving] = useState(false)
  const [removeBusy, setRemoveBusy] = useState(false)
  const [removeError, setRemoveError] = useState(null)

  const handleRemoveOpen = () => {
    setProblem(null)
    setSaved(false)
    setRemoveError(null)
    setRemoving(true)
  }
  const handleRemoveCancel = () => {
    if (removeBusy) return
    setRemoving(false)
    setRemoveError(null)
  }
  const handleRemoveConfirm = async () => {
    setRemoveBusy(true)
    setRemoveError(null)
    try {
      await onDelete()
      setRemoving(false)
      notification && notification.toast && notification.toast.notify(`${Noun} removed`)
    } catch (err) {
      const failure = err || {}
      setRemoveError({ text: serverMessage(failure, `The ${noun} couldn't be removed. Try again in a moment.`), failure })
    } finally {
      setRemoveBusy(false)
    }
  }

  const pageContext = usePageContext()
  const isDark = !!pageContext?.theme?.isDark

  const ratio = `${w} / ${h}`
  const cropping = phase === PHASE.CROPPING
  const saving = phase === PHASE.SAVING

  return (
    <div className="exr-iu" data-phase={phase}>
      <input
        ref={inputRef}
        type="file"
        accept={IMAGE_MIME_TYPES.join(',')}
        className="d-none"
        tabIndex={-1}
        aria-hidden="true"
        data-testid="image-upload-input"
        onChange={handlePicked}
      />

      {!cropping &&
        <div className="exr-iu-current">
          <div className="exr-iu-frame" style={{ aspectRatio: ratio }}>
            {shownURL
              ? <img className={`exr-iu-img ${frameClassName}`} src={shownURL} alt={`Current ${noun}`} />
              : <img className={`exr-iu-img exr-iu-placeholder ${frameClassName}`} src="/content_placeholder.png" alt={`No ${noun} yet`} />}
            {saving &&
              <div className="exr-iu-busy" role="status">
                <span className="spinner-border spinner-border-sm" aria-hidden="true" /> Saving {noun}…
              </div>}
          </div>

          <div className="exr-iu-side">
            {hint && <p className="exr-iu-hint">{hint}</p>}
            {hasCurrent && replaceNote && <p className="exr-iu-hint">{replaceNote}</p>}

            <div className="exr-iu-actions">
              <button type="button" className="btn btn-primary btn-sm" disabled={disabled || saving} onClick={handleChoose}>
                {hasCurrent ? 'Replace…' : 'Upload…'}
              </button>
              {hasCurrent && onDelete &&
                <button type="button" className="btn btn-outline-danger btn-sm" disabled={disabled || saving} onClick={handleRemoveOpen}>
                  Remove
                </button>}
            </div>

            {saved && !saving &&
              <p className="exr-iu-saved" role="status">
                <IconCheck className="me-1" /> {Noun} saved.
              </p>}
            {hasCurrent && !saving && updatedText &&
              <p className="exr-iu-updated" title={dateTimeText(updatedAt)}>last updated {updatedText}</p>}

            {problem &&
              <div className="mt-2">
                <p className="text-danger mb-1" role="alert">{problem.text}</p>
                {problem.failure && <ErrorReference error={problem.failure} />}
              </div>}
          </div>
        </div>}

      {/* Always mounted (Uppy's plugins live with it); shown while cropping. */}
      <div className={`exr-iu-crop ${cropping ? '' : 'd-none'}`} aria-hidden={!cropping}>
        <h5 className="exr-iu-heading">Crop {noun}</h5>
        <p className="exr-iu-hint">
          Drag the frame to choose the part to keep, or drag around it to move the picture; scroll or use + and − to zoom. The frame stays {w}:{h} and is saved at {output.width}×{output.height}.
        </p>
        <div className="exr-iu-stage-row">
          <div className="exr-uppy exr-iu-stage">
            <Dashboard
              uppy={uppy}
              inline
              theme={isDark ? 'dark' : 'light'}
              width="100%"
              height={460}
              hideUploadButton
              disableStatusBar
              disableInformer
              proudlyDisplayPoweredByUppy={false}
              note={config.note}
            />
          </div>
          <div className="exr-iu-preview-col">
            <div className="exr-iu-hint mb-1">Preview</div>
            <div className={`exr-iu-preview ${previewClass}`} style={{ aspectRatio: ratio }} />
          </div>
        </div>
        <div className="exr-iu-actions">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleCancel}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={handleSave}>
            Save {noun}
          </button>
        </div>
      </div>

      {onDelete &&
        <ConfirmDialog
          open={removing}
          title={`Remove ${noun}?`}
          body={removeBody || `The ${noun} is deleted. This can't be undone.`}
          confirmText="Remove"
          busy={removeBusy}
          error={removeError && removeError.text}
          failure={removeError && removeError.failure}
          onConfirm={handleRemoveConfirm}
          onCancel={handleRemoveCancel}
        />}
    </div>
  )
}
